import React, { useEffect, useState, createElement } from 'react';
import { FadeIn } from './FadeIn';
import { Button } from './Button';
import { LegalModal, PrivacyContent, TermsContent } from './LegalModal';
import { trackPageView, trackLead, trackAddToCart } from '../utils/metaPixel';
import { CheckCircle2, XCircle, BookOpen, HeartHandshake, Smartphone, GraduationCap, MessageCircle, FileText, ArrowRight, ShieldCheck } from 'lucide-react';
const COVER_IMAGE = 'https://cdn.magicpatterns.com/uploads/sTLEWt6442GXWDoD5pGoWf/Codigo_Spanglish_tapa.png';
export function LandingPage() {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  useEffect(() => {
    document.title = 'Download now!';
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'Download now!');
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:title');
      meta.setAttribute('content', 'Download now!');
      document.head.appendChild(meta);
    }
    trackPageView();
  }, []);
  return <div className="min-h-screen font-sans bg-brand-cream text-brand-dark selection:bg-brand-green selection:text-white" data-id="element-5">
      {/* HERO SECTION */}
      <section className="bg-brand-dark text-white pt-20 pb-24 px-6 relative overflow-hidden" data-id="element-6">
        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-7">
          <FadeIn data-id="element-8">
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight mb-8 text-brand-sand" data-id="element-9">
              Tu hijo vive en USA, tú en tu cultura latina... y el silencio
              entre ustedes se está volviendo abismo.
            </h1>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-10">
            <h2 className="text-xl md:text-2xl font-light leading-relaxed mb-12 text-gray-300 max-w-3xl mx-auto" data-id="element-11">
              Recupera el respeto y la confianza de tu adolescente en solo 21
              días con el{' '}
              <span className="font-bold text-brand-green" data-id="element-12">
                "Código Spanglish"
              </span>
              , el manual diseñado para padres inmigrantes que ya no saben cómo
              hablar con sus hijos "Americanizados".
            </h2>
          </FadeIn>

          <FadeIn delay={0.4} className="flex flex-col md:flex-row items-center justify-center gap-8" data-id="element-13">
            <img src={COVER_IMAGE} alt="Código Spanglish Book Cover" className="w-64 md:w-80 rounded-lg shadow-2xl shadow-black/50 border border-white/10" data-id="element-14" />
            <div className="text-left max-w-sm" data-id="element-15">
              <Button href="#precio" className="w-full mb-4" onTrack={() => trackLead('hero_cta', '#precio')} data-id="element-16">
                ¡SÍ, QUIERO RECUPERAR A MI HIJO!
              </Button>
              <p className="text-sm text-gray-400 text-center flex items-center justify-center gap-2" data-id="element-17">
                <ShieldCheck size={16} className="text-brand-green" data-id="element-18" />
                Pago seguro y descarga inmediata
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* OPENING HOOK */}
      <section className="py-20 px-6 bg-white" data-id="element-19">
        <div className="max-w-3xl mx-auto" data-id="element-20">
          <FadeIn data-id="element-21">
            <p className="text-xl md:text-2xl leading-relaxed mb-8 font-serif italic text-gray-700" data-id="element-22">
              ¿Te ha pasado que intentas dar un consejo de corazón y tu hijo
              solo te responde con un "whatever" o un portazo? Sientes que lo
              estás perdiendo ante una cultura que no entiendes, un idioma que
              te excluye y un celular que parece haberle robado el alma.
            </p>
          </FadeIn>
          <FadeIn delay={0.2} data-id="element-23">
            <p className="text-lg md:text-xl leading-relaxed text-gray-800" data-id="element-24">
              Trabajaste doble turno y sacrificaste todo para darle el "Sueño
              Americano", pero ahora ese mismo sueño parece estar separándolos.
              No es que tu hijo sea un malagradecido; es que hablan en
              frecuencias distintas.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* THE PROBLEM */}
      <section className="py-20 px-6 bg-brand-sand" data-id="element-25">
        <div className="max-w-3xl mx-auto" data-id="element-26">
          <FadeIn data-id="element-27">
            <h2 className="font-serif text-3xl md:text-4xl mb-8 text-brand-dark font-bold text-center" data-id="element-28">
              El problema no es solo el inglés
            </h2>
          </FadeIn>
          <FadeIn delay={0.2} data-id="element-29">
            <div className="bg-white p-8 md:p-10 rounded-2xl shadow-sm border border-gray-100" data-id="element-30">
              <p className="text-lg mb-6 text-gray-800" data-id="element-31">
                En nuestros países, la autoridad era indiscutible. En Estados
                Unidos, tus hijos están expuestos a una libertad y un
                individualismo que chocan frontalmente con tus valores.
              </p>
              <p className="text-lg mb-6 text-gray-800" data-id="element-32">
                El problema es que tu hijo se refugia en su teléfono para
                escapar de tus juicios, porque siente que{' '}
                <span className="font-semibold" data-id="element-33">
                  "tú no entiendes cómo funcionan las cosas aquí"
                </span>
                .
              </p>
              <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg mt-8" data-id="element-34">
                <p className="text-lg text-red-900 font-medium" data-id="element-35">
                  Si sigues intentando imponer la "chancla" o los gritos, lo
                  único que lograrás es que el silencio en la cena se vuelva
                  permanente.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* WHY WHAT YOU TRIED DIDN'T WORK */}
      <section className="py-20 px-6 bg-white" data-id="element-36">
        <div className="max-w-4xl mx-auto" data-id="element-37">
          <FadeIn data-id="element-38">
            <h2 className="font-serif text-3xl md:text-4xl mb-12 text-center font-bold" data-id="element-39">
              Por qué lo que has intentado no funciona
            </h2>
          </FadeIn>
          <div className="grid gap-6" data-id="element-40">
            {['La jerarquía autoritaria ("porque yo lo digo") solo genera que él se encierre más en su cuarto.', 'Tratar de prohibir el celular por completo lo aísla socialmente y genera un resentimiento profundo.', 'Compararlo con "cómo eran las cosas en mi país" invalida su realidad actual en una escuela americana.', 'El grito como herramienta de poder solo le enseña que tú has perdido el control de la situación.', 'Ignorar el problema esperando que "se le pase la edad" solo ensancha la brecha hasta que sea demasiado tarde.'].map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-41">
                <div className="flex items-start gap-4 bg-brand-cream p-6 rounded-xl" data-id="element-42">
                  <XCircle className="text-red-500 flex-shrink-0 mt-1" size={24} data-id="element-43" />
                  <p className="text-lg text-gray-800" data-id="element-44">{item}</p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* THE UNIQUE MECHANISM */}
      <section className="py-24 px-6 bg-brand-dark text-white" data-id="element-45">
        <div className="max-w-4xl mx-auto text-center" data-id="element-46">
          <FadeIn data-id="element-47">
            <h2 className="font-serif text-3xl md:text-5xl mb-6 text-brand-sand" data-id="element-48">
              El Marco de Traducción de Intenciones (T.I.)
            </h2>
          </FadeIn>
          <FadeIn delay={0.2} data-id="element-49">
            <p className="text-xl leading-relaxed text-gray-300 max-w-3xl mx-auto mb-10" data-id="element-50">
              Este no es un libro de psicología aburrida. Es un sistema de
              "traducción cultural". Aprenderás a identificar qué hay detrás del
              inglés de tu hijo y cómo responder de una manera que desarme su
              actitud defensiva.
            </p>
            <p className="text-2xl font-serif italic text-brand-green max-w-2xl mx-auto" data-id="element-51">
              El "Código Spanglish" te enseña a ser el puente entre sus raíces
              latinas y su futuro americano, sin perder tu esencia de padre.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="py-20 px-6 bg-brand-sand" data-id="element-52">
        <div className="max-w-5xl mx-auto" data-id="element-53">
          <FadeIn data-id="element-54">
            <h2 className="font-serif text-3xl md:text-4xl mb-16 text-center font-bold" data-id="element-55">
              Lo que encontrarás adentro
            </h2>
          </FadeIn>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-56">
            {[{
            icon: Smartphone,
            title: 'Capítulo de Adicción al Celular',
            desc: 'Cómo establecer reglas que funcionen sin peleas diarias.'
          }, {
            icon: HeartHandshake,
            title: 'Guía de Redes Sociales',
            desc: 'Entiende qué hacen en TikTok e Instagram sin parecer un espía.'
          }, {
            icon: GraduationCap,
            title: 'Navegador de Futuro',
            desc: 'Guía básica de GPA, universidades y FAFSA para padres que no conocen el sistema.'
          }, {
            icon: MessageCircle,
            title: 'Diccionario de Emociones',
            desc: 'Frases exactas en español y "spanglish" para abrir la comunicación.'
          }, {
            icon: FileText,
            title: 'Contrato de Conexión Digital',
            desc: 'Un documento listo para imprimir que pondrá orden a las pantallas en tu casa.'
          }].map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-57">
                <div className="bg-white p-8 rounded-2xl shadow-sm h-full border border-gray-100 hover:shadow-md transition-shadow" data-id="element-58">
                  <div className="bg-brand-cream w-14 h-14 rounded-full flex items-center justify-center mb-6" data-id="element-59">
                    <item.icon className="text-brand-green" size={28} data-id="element-60" />
                  </div>
                  <h3 className="font-bold text-xl mb-3" data-id="element-61">{item.title}</h3>
                  <p className="text-gray-600" data-id="element-62">{item.desc}</p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* WHO THIS IS FOR / NOT FOR */}
      <section className="py-20 px-6 bg-white" data-id="element-63">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12" data-id="element-64">
          <FadeIn data-id="element-65">
            <div className="bg-green-50 p-8 md:p-10 rounded-3xl border border-green-100 h-full" data-id="element-66">
              <h3 className="font-serif text-2xl font-bold mb-8 text-green-900 flex items-center gap-3" data-id="element-67">
                <CheckCircle2 className="text-brand-green" data-id="element-68" />
                Esto ES para ti si:
              </h3>
              <ul className="space-y-6" data-id="element-69">
                {['Padres latinos que emigraron a USA buscando un futuro para sus hijos.', 'Padres con hijos de 12 a 18 años nacidos o criados en Estados Unidos.', 'Familias donde el hijo prefiere hablar inglés y el padre se siente ignorado.', 'Padres que ven a sus hijos "perdidos" en el celular todo el día.'].map((item, i) => <li key={i} className="flex items-start gap-3 text-green-800" data-id="element-70">
                    <ArrowRight className="text-brand-green flex-shrink-0 mt-1" size={18} data-id="element-71" />
                    <span data-id="element-72">{item}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-73">
            <div className="bg-red-50 p-8 md:p-10 rounded-3xl border border-red-100 h-full" data-id="element-74">
              <h3 className="font-serif text-2xl font-bold mb-8 text-red-900 flex items-center gap-3" data-id="element-75">
                <XCircle className="text-red-500" data-id="element-76" />
                Esto NO es para ti si:
              </h3>
              <ul className="space-y-6" data-id="element-77">
                {['Padres que creen que la violencia física es la única forma de educar.', 'Personas que no están dispuestas a cambiar su forma de hablar para salvar la relación.', 'Padres que buscan una solución mágica de una noche sin esfuerzo emocional.'].map((item, i) => <li key={i} className="flex items-start gap-3 text-red-800" data-id="element-78">
                    <ArrowRight className="text-red-400 flex-shrink-0 mt-1" size={18} data-id="element-79" />
                    <span data-id="element-80">{item}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 px-6 bg-brand-dark text-white" data-id="element-81">
        <div className="max-w-4xl mx-auto" data-id="element-82">
          <FadeIn data-id="element-83">
            <h2 className="font-serif text-3xl md:text-4xl mb-16 text-center text-brand-sand" data-id="element-84">
              ¿Cómo funciona?
            </h2>
          </FadeIn>
          <div className="space-y-12 relative before:absolute before:inset-0 before:ml-6 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-brand-green before:to-transparent" data-id="element-85">
            {[{
            step: '1',
            title: 'Identifica el Bloqueo',
            desc: 'Usa el test interno para saber por qué tu hijo se alejó realmente.'
          }, {
            step: '2',
            title: 'Aplica el Código',
            desc: 'Sustituye tus frases de ataque por las frases de puente que te entregamos.'
          }, {
            step: '3',
            title: 'Firma el Pacto',
            desc: 'Implementa el Contrato Digital para recuperar las cenas en familia sin teléfonos.'
          }].map((item, i) => <FadeIn key={i} delay={i * 0.2} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-86">
                <div className="flex items-center justify-center w-12 h-12 rounded-full border-4 border-brand-dark bg-brand-green text-white font-bold text-xl shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-xl z-10" data-id="element-87">
                  {item.step}
                </div>
                <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] p-6 rounded-2xl bg-white/5 border border-white/10 ml-6 md:ml-0" data-id="element-88">
                  <h3 className="font-bold text-xl mb-2 text-brand-sand" data-id="element-89">
                    {item.title}
                  </h3>
                  <p className="text-gray-400" data-id="element-90">{item.desc}</p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* PROOF / BELIEVABILITY */}
      <section className="py-20 px-6 bg-brand-green text-white text-center" data-id="element-91">
        <div className="max-w-3xl mx-auto" data-id="element-92">
          <FadeIn data-id="element-93">
            <BookOpen className="mx-auto mb-8 opacity-80" size={48} data-id="element-94" />
            <p className="text-2xl md:text-3xl font-serif leading-relaxed mb-6" data-id="element-95">
              En los próximos 7 días, notarás que la tensión en la casa baja.
            </p>
            <p className="text-lg md:text-xl opacity-90 leading-relaxed" data-id="element-96">
              No porque tu hijo cambió mágicamente, sino porque tú habrás
              aprendido a hablar el idioma de su realidad actual. Verás cómo un
              "whatever" se transforma en una conversación real de 10 minutos.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-6 bg-brand-sand" data-id="element-97">
        <div className="max-w-3xl mx-auto" data-id="element-98">
          <FadeIn data-id="element-99">
            <h2 className="font-serif text-3xl md:text-4xl mb-12 text-center font-bold" data-id="element-100">
              Preguntas Frecuentes
            </h2>
          </FadeIn>
          <div className="space-y-6" data-id="element-101">
            {[{
            q: '¿Mi hijo tiene que leer el libro?',
            a: 'No, el libro es para ti. Tú eres el líder de la casa y el cambio empieza con tu comunicación.'
          }, {
            q: '¿Tengo que saber inglés fluido?',
            a: 'Para nada. El libro está escrito 100% en español para ayudarte a ti.'
          }, {
            q: '¿Funciona si mi hijo ya es un adolescente rebelde?',
            a: 'Sí, especialmente en esos casos, porque la rebeldía suele ser un grito de "no me entiendes".'
          }, {
            q: '¿El libro es físico?',
            a: 'Es un producto digital (PDF) de acceso inmediato para que empieces a leer hoy mismo a las 11 PM si es necesario.'
          }].map((faq, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-102">
                <div className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-gray-100" data-id="element-103">
                  <h4 className="font-bold text-lg mb-3 text-brand-dark" data-id="element-104">
                    {faq.q}
                  </h4>
                  <p className="text-gray-600" data-id="element-105">{faq.a}</p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* OFFER + PRICE BLOCK + CTA */}
      <section id="offer" className="py-24 px-6 bg-white" data-id="element-106">
        <div className="max-w-4xl mx-auto" data-id="element-107">
          <FadeIn data-id="element-108">
            <div id="precio" className="bg-brand-dark rounded-3xl p-8 md:p-12 text-center text-white shadow-2xl relative overflow-hidden" data-id="element-109">
              {/* Decorative background elements */}
              <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none" data-id="element-110">
                <div className="absolute -top-24 -right-24 w-96 h-96 bg-brand-green rounded-full blur-3xl" data-id="element-111"></div>
                <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-brand-green rounded-full blur-3xl" data-id="element-112"></div>
              </div>

              <div className="relative z-10" data-id="element-113">
                <h2 className="font-serif text-3xl md:text-4xl mb-6 text-brand-sand" data-id="element-114">
                  No dejes que el "Sueño Americano" se convierta en la pesadilla
                  de perder a tu hijo.
                </h2>
                <p className="text-xl text-gray-300 mb-10" data-id="element-115">
                  El manual{' '}
                  <span className="font-bold text-white" data-id="element-116">
                    Código Spanglish: Reconexión Padre/Hijo
                  </span>{' '}
                  es tu hoja de ruta definitiva.
                </p>

                <div className="flex flex-col md:flex-row items-center justify-center gap-10 mb-12" data-id="element-117">
                  <img src={COVER_IMAGE} alt="Código Spanglish Book Cover" className="w-48 md:w-64 rounded-lg shadow-2xl shadow-black/50 border border-white/10" data-id="element-118" />
                  <div className="text-center md:text-left" data-id="element-119">
                    <div className="flex items-center gap-3 mb-3" data-id="element-120">
                      <p className="text-sm uppercase tracking-widest text-brand-green font-bold" data-id="element-121">
                        Precio Especial
                      </p>
                      <span className="bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide" data-id="element-122">
                        68% OFF
                      </span>
                    </div>
                    <p className="text-xl text-gray-500 line-through mb-1" data-id="element-123">
                      $19.99 USD
                    </p>
                    <div className="text-6xl font-bold mb-4 font-serif text-white" data-id="element-124">
                      $6<span className="text-2xl text-gray-400" data-id="element-125">.39 USD</span>
                    </div>
                    <p className="text-gray-400 mb-6" data-id="element-126">
                      (Un solo pago, acceso para siempre)
                    </p>
                    <ul className="space-y-3 text-left mb-8" data-id="element-127">
                      <li className="flex items-center gap-2 text-gray-300" data-id="element-128">
                        <CheckCircle2 size={20} className="text-brand-green" data-id="element-129" />{' '}
                        Acceso inmediato (PDF)
                      </li>
                      <li className="flex items-center gap-2 text-gray-300" data-id="element-130">
                        <CheckCircle2 size={20} className="text-brand-green" data-id="element-131" />{' '}
                        Contrato de Conexión Digital
                      </li>
                      <li className="flex items-center gap-2 text-gray-300" data-id="element-132">
                        <CheckCircle2 size={20} className="text-brand-green" data-id="element-133" />{' '}
                        Diccionario de Emociones
                      </li>
                    </ul>
                  </div>
                </div>

                <Button href="https://guiadownload.com/cart/51246980792544:1" className="w-full md:w-auto text-xl py-5 px-10 shadow-brand-green/30 shadow-xl" onTrack={() => trackAddToCart('offer_cta', 'https://guiadownload.com/cart/51246980792544:1')} data-id="element-134">
                  ¡SÍ, QUIERO RECUPERAR A MI HIJO!
                  <span className="block text-sm font-bold mt-1" data-id="element-135">
                    Descarga inmediata
                  </span>
                </Button>

                <p className="mt-6 text-sm text-gray-400 flex items-center justify-center gap-2" data-id="element-136">
                  <ShieldCheck size={16} className="text-brand-green" data-id="element-137" />
                  Pago seguro y descarga inmediata. Haz clic para descargar el
                  manual y empezar la reconexión hoy mismo.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-brand-dark border-t border-white/10 py-8 text-center text-gray-500 text-sm" data-id="element-138">
        <div className="flex items-center justify-center gap-4 mb-3" data-id="element-139">
          <button onClick={() => setPrivacyOpen(true)} className="underline hover:text-gray-300 transition-colors" data-id="element-140">
            Privacy Policy
          </button>
          <span className="text-gray-600" data-id="element-141">|</span>
          <button onClick={() => setTermsOpen(true)} className="underline hover:text-gray-300 transition-colors" data-id="element-142">
            Terms of Service
          </button>
        </div>
        <p data-id="element-143">
          © {new Date().getFullYear()} Código Spanglish. Todos los derechos
          reservados.
        </p>
      </footer>

      <LegalModal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Política de Privacidad" data-id="element-144">
        <PrivacyContent data-id="element-145" />
      </LegalModal>
      <LegalModal isOpen={termsOpen} onClose={() => setTermsOpen(false)} title="Términos de Servicio" data-id="element-146">
        <TermsContent data-id="element-147" />
      </LegalModal>
    </div>;
}